(() => {
  'use strict';
  if (window.HTFlashSaleGift) return;
  const node = document.getElementById('FlashSaleGiftConfig');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch { return; }
  const marker = '_flash_sale_gift';
  const buyers = new Set((config.buyProductIds || []).map(String));
  const gift = config.gift;
  const active = config.enabled === true && config.complete === true && buyers.size > 0 && gift &&
    Number.isInteger(config.buyQuantity) && config.buyQuantity > 0 &&
    Number.isInteger(config.giftQuantity) && config.giftQuantity > 0 &&
    !buyers.has(String(gift.productId));
  const root = config.root || '/';
  let running = null;
  let dirty = false;
  let publishing = false;
  let checkoutReplay = false;
  let failedSignature = null;
  let timer;
  const managed = item => item.properties?.[marker] === 'v1';
  const signature = cart => JSON.stringify(cart.items.filter(item => !managed(item)).map(item =>
    [item.key, item.quantity, item.final_line_price]).concat([cart.discount_codes || []]));

  async function request(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { method: 'POST', body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error(data.description || 'Cart could not be updated. Please try again.');
    return data;
  }

  function notice(message) {
    let element = document.getElementById('FlashSaleGiftStatus');
    if (!element) {
      element = document.createElement('div');
      element.id = 'FlashSaleGiftStatus';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      element.style.cssText = 'position:fixed;bottom:20px;left:4%;width:92%;max-width:420px;padding:12px;background:#111;color:#fff;z-index:99999;border-radius:8px;font-size:14px';
      document.body.appendChild(element);
    }
    element.textContent = message;
    element.hidden = false;
    clearTimeout(timer);
    timer = setTimeout(() => { element.hidden = true; }, 7000);
  }

  function sectionIds() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
    return [...new Set(sections.filter(Boolean))].slice(0, 5);
  }

  async function refresh(cart) {
    const sections = sectionIds();
    const bus = window.theme?.pubsub;
    if (sections.length && bus?.publish && bus.PUB_SUB_EVENTS?.cartUpdate) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('sections', sections.join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Cart display could not refresh. Please try again.');
      const html = await response.json();
      if (sections.some(id => typeof html[id] !== 'string')) throw new Error('Cart display could not refresh. Please try again.');
      publishing = true;
      try { bus.publish(bus.PUB_SUB_EVENTS.cartUpdate, { source: 'flash-sale-gift', cart: { ...cart, sections: html } }); }
      finally { publishing = false; }
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    }
    document.querySelectorAll('cart-count').forEach(element => {
      element.textContent = String(cart.item_count || 0);
      element.toggleAttribute('hidden', !cart.item_count);
    });
  }

  async function reconcile() {
    let changed = false;
    let cart = await request('cart.js');
    for (let round = 0; round < 8; round += 1) {
      const gifts = cart.items.filter(managed);
      const quantity = cart.items.reduce((total, item) => total + (
        !managed(item) && buyers.has(String(item.product_id)) && !item.selling_plan_allocation ? item.quantity : 0), 0);
      const desired = active && quantity >= config.buyQuantity ? config.giftQuantity : 0;
      const current = gifts.length === 1 ? gifts[0] : null;
      const valid = current && String(current.variant_id) === String(gift?.variantId) &&
        current.quantity === desired && current.final_line_price === 0 && !current.selling_plan_allocation;
      if (valid || (!desired && !gifts.length) || (!gifts.length && failedSignature === signature(cart))) {
        if (changed) await refresh(cart);
        return cart;
      }
      if (gifts.length) {
        const charged = gifts.some(item => item.final_line_price !== 0);
        const updates = Object.fromEntries(gifts.map(item => [item.key, 0]));
        cart = await request('cart/update.js', { updates });
        changed = true;
        if (charged) {
          failedSignature = signature(cart);
          notice('The free-gift discount is unavailable. The gift was removed so you are not charged for it.');
        }
        continue;
      }
      try {
        await request('cart/add.js', { items: [{ id: gift.variantId, quantity: desired, properties: { [marker]: 'v1' } }] });
        changed = true;
      } catch (error) {
        cart = await request('cart.js');
        if (cart.items.some(managed)) { changed = true; continue; }
        failedSignature = signature(cart);
        notice('Your items are in the cart, but the flash-sale gift could not be added. Please try again or contact us.');
        if (changed) await refresh(cart);
        return cart;
      }
      cart = await request('cart.js');
    }
    throw new Error('The cart is still changing. Please try checkout again.');
  }

  function sync() {
    dirty = true;
    if (running) return running;
    running = (async () => {
      let cart;
      let rounds = 0;
      while (dirty) {
        if (++rounds > 8) throw new Error('The cart is still changing. Please try checkout again.');
        dirty = false;
        cart = navigator.locks?.request
          ? await navigator.locks.request('hostage-flash-sale-gift', reconcile)
          : await reconcile();
      }
      return cart;
    })().finally(() => { running = null; });
    return running;
  }

  const schedule = () => {
    if (!publishing) sync().catch(error => { console.error('[Flash sale gift]', error); notice(error.message); });
  };
  window.HTFlashSaleGift = { sync };
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('ht:starter-addon-added', schedule);
  document.addEventListener('ht:starter-addon-removed', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);

  async function guard(event, replay) {
    if (checkoutReplay) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await sync();
      checkoutReplay = true;
      try { replay(); } finally { checkoutReplay = false; }
    } catch (error) {
      notice('Please wait before checkout: ' + error.message);
    }
  }

  document.addEventListener('click', event => {
    const control = event.target.closest?.('[name="checkout"], a[href*="/checkout"]');
    if (control) guard(event, () => control.click());
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(new URL(form.action, location.href).pathname)) {
      guard(event, () => form.requestSubmit(event.submitter || undefined));
    }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();
